import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { useNodeThemeColors } from "@/components/expression/theme/useNodeTheme";
import { useSymbolPushTailwindClassName } from "@/components/expression/utils/layout/useSymbolPushTailwindClassName";
import { hasMatrixBounds, hasVectorBounds } from "@/components/expression/utils/matrix";
import { Icon } from "@/components/icons/Icon";
import { groupsJson } from "@/components/suggestions/providers/data";
import { twMerge } from "@/utils/twMerge";

import type { NodeModel } from "@/models/nodes/types";
import type { HintKey } from "@/store/HintsStore";
import type { PropsWithChildren } from "react";

const Symbol = observer(function Symbol({
  node,
  children,
  className,
}: PropsWithChildren<{ node: NodeModel; className?: string }>) {
  const theme = useNodeThemeColors(node);

  return (
    <>
      {children}&nbsp;
      {"value" in node && (
        <>
          <span className="text-blue-500">•</span>&nbsp;
          <span
            className={twMerge(
              "text-gray-500",
              theme?.symbol && `text-${theme.symbol.light} dark:text-${theme.symbol.dark}`,
              className,
            )}
          >
            {node.value}
          </span>
        </>
      )}
    </>
  );
});

type Props = {
  node: NodeModel;
  hideSymbol?: boolean;
};

export const ActiveNodeTitle = observer(function ActiveNodeTitle({
  node,
  hideSymbol,
  children,
}: PropsWithChildren<Props>) {
  switch (node.type) {
    case "variable":
    case "constant":
    case "function": {
      if (hideSymbol) {
        return children;
      }

      return (
        <Symbol node={node} className={twMerge(node.type !== "function" && "normal-case")}>
          {children}
        </Symbol>
      );
    }

    default: {
      return children;
    }
  }
});

const FunctionTriggerIcon = ({ node: { value } }: { node: NodeModel<"function"> }) => {
  const isTwoLines = value.length > 4;
  const isFit = value.length < 7;

  return (
    <div className="w-6 h-6 flex items-center justify-center">
      <div
        className={twMerge("flex items-center justify-center", isTwoLines ? "max-w-[27px]" : "w-7")}
      >
        <span
          className={twMerge(
            "font-mono text-[13px] -tracking-[8%]",
            isTwoLines && "wrap-break-word line-clamp-2 leading-[11px]",
            isFit && "text-center",
          )}
        >
          {value}
        </span>
      </div>
    </div>
  );
};

const VariableTriggerIcon = observer(
  ({ node: { value, color } }: { node: NodeModel<"variable" | "constant"> }) => {
    const symbolPushClassName = useSymbolPushTailwindClassName(value);
    const codePoint = value?.codePointAt(0);
    if (codePoint === undefined) return null;

    return (
      <div className="w-6 h-6 flex items-center justify-center">
        <div
          className={twMerge(
            `flex items-center justify-center rounded-5 shadow-node-outline px-1 min-h-[21px] min-w-[17px]`,
            `bg-${color}-200 dark:bg-${color}-800`,
          )}
        >
          <span
            className={twMerge(
              "font-mono text-node-sm",
              `text-${color}-900 dark:text-${color}-100`,
              symbolPushClassName?.negative,
            )}
          >
            {String.fromCodePoint(codePoint)}
          </span>
        </div>
      </div>
    );
  },
);

const BracketsTriggerIcon = observer(({ node }: { node: NodeModel<"group"> }) => {
  const activeGroup = Object.values(groupsJson).find((group) => {
    return (
      node.openingSymbol === group.openingSymbol.symbol &&
      node.closingSymbol === group.closingSymbol.symbol
    );
  });

  return <Icon name={activeGroup?.icon || "math/group/RoundBrackets"} />;
});

const NumberTriggerIcon = ({ node: { value } }: { node: NodeModel<"number"> }) => {
  const limit = 3;
  let valueToDisplay = value;
  if (value.length > limit) {
    valueToDisplay = value.substring(0, limit - 1);
    if (valueToDisplay.at(-1) === ".") valueToDisplay = valueToDisplay.slice(0, -1);
    valueToDisplay += "…";
  }

  return (
    <div className="w-6 h-6 flex items-center justify-center">
      <span className="font-mono text-[13px] -tracking-[8%]">{valueToDisplay}</span>
    </div>
  );
};

export function useActiveNodeTypeTitle(node: NodeModel): {
  title: string;
  hintKey?: HintKey;
  triggerIcon?: React.ReactNode;
} {
  const t = useTranslations("sidebar.activeNode.title");

  switch (node.type) {
    case "variable": {
      return {
        title: node.hasParams ? t("function") : t("variable"),
        hintKey: node.hasParams ? "activeFunction" : "activeVariable",
        triggerIcon: <VariableTriggerIcon node={node} />,
      };
    }

    case "constant": {
      return {
        title: t("constant"),
        hintKey: "activeConstant",
        triggerIcon: <VariableTriggerIcon node={node} />,
      };
    }

    case "function": {
      return {
        title: t("function"),
        hintKey: "activeFunction",
        triggerIcon: <FunctionTriggerIcon node={node} />,
      };
    }

    case "group": {
      const { bounds } = node.matrixNode ?? {};

      if (node.casesSystemEnabled) {
        return {
          title: t("casesSystem"),
          hintKey: "activeCasesSystem",
          triggerIcon: <Icon name="math/CasesSystem" />,
        };
      }

      if (!bounds) {
        return {
          title: t("group"),
          hintKey: "activeGroup",
          triggerIcon: <BracketsTriggerIcon node={node} />,
        };
      }

      if (hasVectorBounds(bounds)) {
        return {
          title: t("vector"),
          hintKey: "activeVector",
          triggerIcon: <BracketsTriggerIcon node={node} />,
        };
      }

      if (hasMatrixBounds(bounds)) {
        return {
          title: t("matrix"),
          hintKey: "activeMatrix",
          triggerIcon: <BracketsTriggerIcon node={node} />,
        };
      }

      return {
        title: t("group"),
        hintKey: "activeGroup",
        triggerIcon: <BracketsTriggerIcon node={node} />,
      };
    }

    case "number": {
      return {
        title: t("number"),
        hintKey: "activeNumber",
        triggerIcon: <NumberTriggerIcon node={node} />,
      };
    }

    case "placeholder": {
      return {
        title: t("placeholder"),
        triggerIcon: <Icon name="Placeholder" width={24} height={24} />,
      };
    }

    default: {
      return { title: t("node") };
    }
  }
}
